import {AnimatePresence, m} from 'framer-motion';
import {Fragment} from 'react';
import {opacityAnimation} from '@common/ui/animation/opacity-animation';
import {Skeleton} from '@common/ui/skeleton/skeleton';
import {useProducts} from '@common/billing/pricing-table/use-products';
import {Product} from '@common/billing/product';
import {
  findBestPrice,
  UpsellBillingCycle,
} from '@common/billing/pricing-table/find-best-price';
import {useAuth} from '@common/auth/use-auth';
import clsx from 'clsx';
import {Chip} from '@common/ui/forms/input-field/chip-field/chip';
import {Trans} from '@common/i18n/trans';
import {FormattedPrice} from '@common/i18n/formatted-price';
import {Button} from '@common/ui/buttons/button';
import {Link} from 'react-router-dom';
import {setInLocalStorage} from '@common/utils/hooks/local-storage';
import {ProductFeatureList} from '@common/billing/pricing-table/product-feature-list';

interface PricingTableProps {
  selectedCycle: UpsellBillingCycle;
  className?: string;
  productLoader?: string;
  variant?: 'default' | 'landing';
}
export function PricingTable({
  selectedCycle,
  className,
  productLoader,
  variant = 'default',
}: PricingTableProps) {
  const query = useProducts(productLoader);
  return (
    <div
      className={clsx(
        'flex flex-col items-stretch gap-24 overflow-x-auto overflow-y-visible pb-20 md:flex-row md:justify-center',
        className,
      )}
    >
      <AnimatePresence initial={false} mode="wait">
        {query.data ? (
          <PlanList
            key="plan-list"
            plans={query.data.products}
            selectedPeriod={selectedCycle}
            variant={variant}
          />
        ) : (
          <SkeletonLoader key="skeleton-loader" variant={variant} />
        )}
      </AnimatePresence>
    </div>
  );
}

interface PlanListProps {
  plans: Product[];
  selectedPeriod: UpsellBillingCycle;
  variant: 'default' | 'landing';
}
function PlanList({plans, selectedPeriod, variant}: PlanListProps) {
  const {isLoggedIn, isSubscribed, user} = useAuth();
  const filteredPlans = plans.filter(plan => !plan.hidden);
  const currentSubscription = user?.subscriptions?.find(sub => sub.valid);
  const currentProductId = currentSubscription?.product_id;
  return (
    <Fragment>
      {filteredPlans.map((plan, index) => {
        const isFirst = index === 0;
        const isLast = index === filteredPlans.length - 1;
        const price = findBestPrice(selectedPeriod, plan.prices);
        const isCurrentPlan =
          currentProductId === plan.id ||
          (isLoggedIn && !isSubscribed && Boolean(plan.free));
        const canCheckout = isLoggedIn && !isSubscribed && !plan.free && price;
        const canChangePlan =
          isSubscribed && !isCurrentPlan && !plan.free && price;
        const directPlanChangeUnsupported =
          canChangePlan && currentSubscription?.gateway_name === 'mercado_pago';

        let upgradeRoute;
        if (!isLoggedIn) {
          upgradeRoute = `/register?redirectFrom=pricing`;
        }
        if (canChangePlan) {
          upgradeRoute = directPlanChangeUnsupported
            ? '/billing'
            : `/billing/change-plan/${plan.id}/${price?.id}/confirm`;
        }
        if (canCheckout) {
          upgradeRoute = `/checkout/${plan.id}/${price?.id}`;
        }

        return (
          <m.div
            key={plan.id}
            {...opacityAnimation}
            className={clsx(
              'w-full rounded-panel border px-28 py-28 shadow-lg md:min-w-240 md:max-w-350',
              variant === 'default' && 'bg',
              variant === 'landing' &&
                'relative overflow-hidden border-white/10 bg-white/[0.045] text-white shadow-[0_24px_80px_rgb(0_0_0/0.24)] backdrop-blur',
              variant === 'landing' &&
                plan.recommended &&
                'border-primary/70 bg-primary/10 shadow-primary/10',
              isFirst && 'ml-auto',
              isLast && 'mr-auto',
            )}
          >
            <div className="mb-32">
              <Chip
                radius="rounded"
                size="sm"
                className={clsx(
                  'mb-20 w-min',
                  !plan.recommended && 'invisible',
                  variant === 'landing' &&
                    'border-primary/30 bg-primary/15 text-primary',
                )}
              >
                <Trans message="Mais escolhido" />
              </Chip>
              <div className="mb-12 text-xl font-semibold">
                <Trans message={plan.name} />
              </div>
              <div
                className={clsx(
                  'text-sm',
                  variant === 'default' ? 'text-muted' : 'text-white/68',
                )}
              >
                <Trans message={plan.description} />
              </div>
            </div>
            <div>
              {price ? (
                <FormattedPrice
                  priceClassName={clsx(
                    'font-bold text-4xl',
                    variant === 'landing' && 'text-white',
                  )}
                  periodClassName={clsx(
                    'text-xs',
                    variant === 'default' ? 'text-muted' : 'text-white/56',
                  )}
                  variant="separateLine"
                  price={price}
                />
              ) : (
                <div className="text-4xl font-bold">
                  <Trans message="Grátis" />
                </div>
              )}
              <div className="mt-60">
                <Button
                  variant={plan.recommended ? 'flat' : 'outline'}
                  color="primary"
                  className="w-full"
                  size="md"
                  elementType={upgradeRoute ? Link : undefined}
                  disabled={!upgradeRoute}
                  onClick={() => {
                    if (isLoggedIn || !price || !plan) return;
                    setInLocalStorage('be.onboarding.selected', {
                      productId: plan.id,
                      priceId: price.id,
                    });
                  }}
                  to={upgradeRoute}
                >
                  {isCurrentPlan ? (
                    <Trans message="Plano atual" />
                  ) : directPlanChangeUnsupported ? (
                    <Trans message="Gerenciar assinatura" />
                  ) : plan.free ? (
                    <Trans message="Começar" />
                  ) : (
                    <Trans message="Assinar" />
                  )}
                </Button>
              </div>
              <ProductFeatureList product={plan} variant={variant} />
            </div>
          </m.div>
        );
      })}
    </Fragment>
  );
}

function SkeletonLoader({variant}: {variant: 'default' | 'landing'}) {
  return (
    <Fragment>
      <PlanSkeleton key="skeleton-1" variant={variant} />
      <PlanSkeleton key="skeleton-2" variant={variant} />
      <PlanSkeleton key="skeleton-3" variant={variant} />
    </Fragment>
  );
}

function PlanSkeleton({variant}: {variant: 'default' | 'landing'}) {
  return (
    <m.div
      {...opacityAnimation}
      className={clsx(
        'w-full rounded-lg border px-28 py-90 shadow-lg md:max-w-350',
        variant === 'landing' && 'border-white/10 bg-white/[0.045]',
      )}
    >
      <Skeleton className="my-10" />
      <Skeleton className="mb-40" />
      <Skeleton className="mb-40 h-30" />
      <Skeleton className="mb-40 h-40" />
      <Skeleton className="mb-20" />
      <Skeleton />
      <Skeleton />
    </m.div>
  );
}
